import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Bookmark, BookmarkX, Play, ArrowLeft, MapPin } from "lucide-react";
import api from "../lib/apiClient";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import Footer from "../components/Footer";

/**
 * TrailPage — the viewer-facing list of every video the user has
 * saved to their personal Trail. Backed by `GET /api/users/me/trail`
 * (joined to the videos collection so we get full playable rows).
 *
 * Why a dedicated route?
 *   The bookmark icon inside the FeedPlayerOverlay already lets users
 *   add/remove from the trail, but until now there was no surface
 *   that showed the *result*. Producers can hint at viewers' trail
 *   counts as social proof; viewers can rewatch their favourites
 *   without scrolling the global feed for them.
 *
 * Layout:
 *   - Sticky header with back button + title + count
 *   - Empty state with an inline CTA when no saves
 *   - Card grid: thumbnail (autoplaying on hover), title, producer,
 *     "saved on" date, click-to-watch + click-to-remove
 *
 * Authentication:
 *   Route is wrapped in <ProtectedRoute> at the app shell — anonymous
 *   users get redirected to /auth. We still defensively check `user`
 *   here so a session expiry mid-page doesn't crash render.
 */
const TrailPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [trail, setTrail] = useState([]);
  const [loading, setLoading] = useState(true);
  const [removingIds, setRemovingIds] = useState(() => new Set());

  useEffect(() => {
    if (!user) return undefined;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await api.get(`/users/me/trail`);
        if (!cancelled) setTrail(Array.isArray(res.data?.trail) ? res.data.trail : []);
      } catch (err) {
        if (!cancelled) {
          console.error("Failed to load trail:", err);
          toast.error(err.response?.data?.detail || "Couldn't load your Trail");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  const handleRemove = async (videoId, e) => {
    e?.stopPropagation?.();
    // Optimistic — pull the row immediately so the grid feels snappy.
    const prev = trail;
    setRemovingIds(s => new Set(s).add(videoId));
    setTrail(t => t.filter(v => v.id !== videoId));
    try {
      const res = await api.post(`/videos/${videoId}/trail`, {});
      // Server returned saved=true → unexpected (was supposed to toggle
      // off). Roll back and show a hint.
      if (res.data?.saved) {
        setTrail(prev);
        toast.error("Couldn't remove — try again in a moment");
      } else {
        toast.success("Removed from your Trail");
      }
    } catch (err) {
      // Roll back on error.
      setTrail(prev);
      toast.error(err.response?.data?.detail || "Couldn't update Trail");
    } finally {
      setRemovingIds(s => {
        const next = new Set(s);
        next.delete(videoId);
        return next;
      });
    }
  };

  const formatSavedAt = (iso) => {
    try {
      return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    } catch {
      return "";
    }
  };

  return (
    <div className="min-h-screen w-full bg-gradient-to-b from-gray-950 to-black text-white" data-testid="trail-page">
      <div className="max-w-6xl mx-auto px-4 pt-6 pb-24">
        <div className="flex items-center gap-3 mb-6">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(-1)}
            className="text-gray-400 hover:text-white"
            data-testid="trail-back-btn"
          >
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Button>
          <div className="flex items-center gap-2">
            <Bookmark className="h-5 w-5 text-emerald-400" />
            <h1 className="text-xl sm:text-2xl font-semibold tracking-tight">My Trail</h1>
          </div>
          {!loading && (
            <span
              className="ml-auto text-xs text-gray-500"
              data-testid="trail-count"
            >
              {trail.length} saved
            </span>
          )}
        </div>

        {loading ? (
          <div className="text-center text-gray-500 py-16" data-testid="trail-loading">
            Loading your Trail…
          </div>
        ) : trail.length === 0 ? (
          <div className="text-center py-16 bg-gray-900/40 border border-gray-800 rounded-lg" data-testid="trail-empty-state">
            <Bookmark className="h-12 w-12 mx-auto text-gray-700 mb-3" />
            <h2 className="text-lg font-medium text-gray-200 mb-2">Your Trail is empty</h2>
            <p className="text-sm text-gray-500 mb-4 max-w-md mx-auto">
              While you watch the feed, tap the bookmark icon on any video to save it here.
              Your trail is private — only you can see what's in it.
            </p>
            <Button
              onClick={() => navigate("/")}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="trail-go-to-feed-btn"
            >
              <Play className="h-4 w-4 mr-2" /> Watch the feed
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="trail-grid">
            {trail.map((video) => {
              const removing = removingIds.has(video.id);
              return (
                <Card
                  key={video.id}
                  className="bg-gray-900 border-gray-800 overflow-hidden group hover:border-emerald-500/50 transition-all"
                  data-testid={`trail-card-${video.id}`}
                >
                  <div
                    className="relative aspect-video bg-black cursor-pointer"
                    onClick={() => navigate(`/video/${video.id}`)}
                  >
                    <video
                      src={video.video_url}
                      className="w-full h-full object-cover"
                      loop muted playsInline preload="metadata"
                      onMouseEnter={(e) => e.target.play().catch(() => {})}
                      onMouseLeave={(e) => { e.target.pause(); e.target.currentTime = 0; }}
                    />
                    <div className="absolute bottom-2 left-2 text-white text-[10px] font-mono bg-black/50 px-2 py-0.5 rounded flex items-center gap-1">
                      <MapPin className="h-2.5 w-2.5" />
                      <span className="truncate max-w-[140px]">
                        {video.location?.city || "Unknown"}, {video.location?.country || ""}
                      </span>
                    </div>
                    <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/10 transition-colors pointer-events-none">
                      <Play className="h-10 w-10 text-white/80" />
                    </div>
                  </div>
                  <CardContent className="p-3">
                    <h3 className="font-semibold text-white truncate text-sm" data-testid={`trail-card-title-${video.id}`}>
                      {video.title}
                    </h3>
                    <div className="flex items-center justify-between mt-1">
                      <Link
                        to={`/${video.username}`}
                        className="text-emerald-400 hover:underline text-xs"
                        onClick={(e) => e.stopPropagation()}
                      >
                        @{video.username}
                      </Link>
                      <span className="text-[10px] text-gray-500">
                        saved {formatSavedAt(video.trail_saved_at)}
                      </span>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => handleRemove(video.id, e)}
                      disabled={removing}
                      className="w-full mt-2 text-xs text-gray-400 hover:text-rose-300 hover:bg-rose-900/20"
                      data-testid={`trail-remove-btn-${video.id}`}
                    >
                      <BookmarkX className="h-3.5 w-3.5 mr-1" />
                      {removing ? "Removing…" : "Remove from Trail"}
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default TrailPage;
